import { useNavigate, useParams } from 'react-router-dom';
import { Boxes, ShieldAlert } from 'lucide-react';
import { Container, PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { CompactStat, CompactStatGroup } from '../components/CompactStat';
import { Button, Skeleton, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useZoneResources } from '@/api/opennebula/zones';
import { CapacityMeters } from './components/CapacityMeters';
import { HostsTable } from './components/HostsTable';
import { DatastoresTable } from './components/DatastoresTable';
import { NetworksTable } from './components/NetworksTable';

const BASE = '/services/private-zones';

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/**
 * Zone resource dashboard (/services/private-zones/:id): summary counts, capacity
 * meters and tabs for hosts, datastores and networks. 401/403 → access-denied.
 */
export function PrivateZoneDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useZoneResources(id);

  const status = statusOf(error);
  const denied = status === 401 || status === 403;
  const zoneName = data?.zone?.name ?? id ?? 'Zone';

  if (!isLoading && denied) {
    return (
      <Container size="xl">
        <PageHeader icon={<Boxes size={22} />} title="Private Zone" />
        <EmptyState
          icon={<ShieldAlert size={28} />}
          title="Access denied"
          description="You do not have permission to view resources for this zone. Only the zone owner can access this page."
          actions={<Button variant="secondary" onClick={() => navigate(BASE)}>Back to My Zones</Button>}
        />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={BASE} onClick={(e) => { e.preventDefault(); navigate(BASE); }}>Private Zones</BreadcrumbItem>
            <BreadcrumbItem current>{String(zoneName)}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={String(zoneName)}
        description="Zone resources overview — hosts, datastores, networks and capacity."
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
        }
      />

      {isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : error && !denied ? (
        <Alert
          tone="danger"
          title="Failed to load zone resources"
          actions={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}
        >
          An error occurred while fetching zone data. Please try again.
        </Alert>
      ) : data ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Section style={{ marginBottom: 0 }} title="Summary">
            <CompactStatGroup columns={4}>
              <CompactStat label="Hosts" value={data.hosts?.length ?? 0} />
              <CompactStat label="Clusters" value={data.clusters?.length ?? 0} />
              <CompactStat label="Datastores" value={data.datastores?.length ?? 0} />
              <CompactStat label="Networks" value={data.networks?.length ?? 0} />
            </CompactStatGroup>
          </Section>

          <Section style={{ marginBottom: 0 }} title="Capacity" description="Overall compute, memory and storage utilization for this zone.">
            <CapacityMeters capacity={data.capacity ?? {}} />
          </Section>

          <Section style={{ marginBottom: 0 }} title="Resources">
            <Tabs defaultValue="hosts">
              <TabsList>
                <TabsTrigger value="hosts">Hosts ({data.hosts?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="datastores">Datastores ({data.datastores?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="networks">Networks ({data.networks?.length ?? 0})</TabsTrigger>
              </TabsList>
              <TabsContent value="hosts">
                <HostsTable hosts={data.hosts ?? []} />
              </TabsContent>
              <TabsContent value="datastores">
                <DatastoresTable datastores={data.datastores ?? []} />
              </TabsContent>
              <TabsContent value="networks">
                <NetworksTable networks={data.networks ?? []} />
              </TabsContent>
            </Tabs>
          </Section>
        </div>
      ) : null}
    </Container>
  );
}

